import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  BannerType,
  MessageBanner,
} from "@via/components/message-banner/message-banner";
import SelectComponent from "@via/components/select/select";
import { Spinner } from "@via/components/loading/loading";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { NOTIFICATION } from "@via/context/notifications-context";
import type { SelectOption } from "@via/utils/interfaces.tsx";
import {
  EVENT_HAS_UNKEPT_PARTS_MESSAGE,
  type EventEditorCollection,
  type EventEditorOptions,
  type EventSectionInput,
} from "common";
import { type ReactNode, useEffect, useReducer, useRef, useState } from "react";
import type { Options } from "react-select";
import {
  eventErrorMessage,
  eventErrorStatus,
  useCreateVmetaEvent,
  useUpdateVmetaEvent,
  useVmetaEventDefinition,
  useVmetaEventEditorOptions,
} from "@via/pages/projects/metadata/events/events.api";
import {
  definitionReducer,
  definitionStateFrom,
  emptyDefinitionState,
  inputFromState,
  type DefinitionAction,
  type DefinitionFormState,
} from "./definition-form-state";

const NEW_FORM = "new";

const buttonClass =
  "rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50";
const primaryClass =
  "rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50";

const multiValues = (selected: SelectOption | Options<SelectOption>) =>
  (Array.isArray(selected) ? (selected as SelectOption[]) : []).map(o =>
    String(o.value)
  );

/** The option of each of `ids`, in the order of `ids`, so a stored order is never rewritten. */
const optionsOf = <T extends { value: string; label: string }>(
  ids: string[],
  all: T[]
): T[] => ids.flatMap(id => all.filter(o => o.value === id));

const Checkbox = ({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <label className="flex items-center gap-2 text-sm text-gray-700">
    <input
      type="checkbox"
      className="h-4 w-4 rounded border-gray-300 text-indigo-600"
      checked={checked}
      onChange={e => onChange(e.target.checked)}
    />
    {label}
  </label>
);

interface SectionCardProps {
  index: number;
  section: EventSectionInput;
  collection: EventEditorCollection | undefined;
  /** 1 for the first section of its collection, 2 for the second, and so on. */
  ordinal: number;
  isFirst: boolean;
  isLast: boolean;
  state: DefinitionFormState;
  options: EventEditorOptions;
  dispatch: (action: DefinitionAction) => void;
}

const SectionCard = ({
  index,
  section,
  collection,
  ordinal,
  isFirst,
  isLast,
  state,
  options,
  dispatch,
}: SectionCardProps) => {
  const label = collection?.label || collection?.name || section.collectionId;
  const fieldOptions = (collection?.fields ?? []).map(f => ({
    value: f.id,
    label: f.label || f.name,
  }));
  const chosenOptions = optionsOf(section.fieldIds, fieldOptions);
  const sourceOptions = options.apiSources.map(s => ({
    value: s.id,
    label: s.label,
  }));
  const patch = (p: Partial<Omit<EventSectionInput, "collectionId">>) =>
    dispatch({ type: "setSection", index, patch: p });
  const setRule = (r: number, ids: string[]) =>
    patch({
      multicolumn: section.multicolumn.map((rule, i) => (i === r ? ids : rule)),
    });
  const apiTable = state.apiTable;

  return (
    <div className="rounded-md border border-gray-200 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-900">
          {label}{" "}
          <span className="font-normal text-gray-500">Section {ordinal}</span>
        </h4>
        <span className="flex gap-2">
          <button
            type="button"
            className={buttonClass}
            disabled={isFirst}
            aria-label="Move up"
            onClick={() =>
              dispatch({ type: "moveSection", index, direction: -1 })
            }>
            Up
          </button>
          <button
            type="button"
            className={buttonClass}
            disabled={isLast}
            aria-label="Move down"
            onClick={() =>
              dispatch({ type: "moveSection", index, direction: 1 })
            }>
            Down
          </button>
          <button
            type="button"
            className={buttonClass}
            aria-label="Remove section"
            onClick={() => dispatch({ type: "removeSection", index })}>
            Remove
          </button>
        </span>
      </div>
      {ordinal > 1 && (
        <p className="text-sm text-gray-500">
          This section fills the same record as the first {label} section.
        </p>
      )}
      <div>
        <p className="text-sm font-medium text-gray-900">Fields</p>
        <SelectComponent
          name={`section-${index}-fields`}
          isMulti
          options={fieldOptions}
          value={chosenOptions}
          onChange={selected => patch({ fieldIds: multiValues(selected) })}
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Checkbox
          label="Add records"
          checked={section.insert}
          onChange={insert => patch({ insert })}
        />
        <Checkbox
          label="Change records"
          checked={section.update}
          onChange={update => patch({ update })}
        />
        <Checkbox
          label="Allow more than one"
          checked={section.multiple}
          onChange={multiple => patch({ multiple })}
        />
        <Checkbox
          label="Collapsible"
          checked={section.collapsible}
          onChange={collapsible => patch({ collapsible })}
        />
        <Checkbox
          label="Collapsed when the form opens"
          checked={section.collapseOnLoad}
          onChange={collapseOnLoad => patch({ collapseOnLoad })}
        />
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-gray-900">Fields in one row</p>
          <button
            type="button"
            className={buttonClass}
            onClick={() =>
              patch({ multicolumn: [...section.multicolumn, []] })
            }>
            + Add row
          </button>
        </div>
        {section.multicolumn.map((rule, r) => (
          <div key={r} className="flex items-center gap-2">
            <div className="flex-1">
              <SelectComponent
                name={`section-${index}-row-${r}`}
                isMulti
                options={chosenOptions}
                value={optionsOf(rule, chosenOptions)}
                onChange={selected => setRule(r, multiValues(selected))}
              />
            </div>
            <button
              type="button"
              className={buttonClass}
              aria-label="Remove row"
              onClick={() =>
                patch({
                  multicolumn: section.multicolumn.filter((_, i) => i !== r),
                })
              }>
              Remove
            </button>
          </div>
        ))}
      </div>
      {index === 0 && (
        <div className="space-y-2 border-t border-gray-100 pt-3">
          <p className="text-sm font-medium text-gray-900">API tables</p>
          <SelectComponent
            name={`section-${index}-api-tables`}
            isMulti
            options={sourceOptions}
            value={optionsOf(apiTable?.sourceIds ?? [], sourceOptions)}
            onChange={selected => {
              const sourceIds = multiValues(selected);
              dispatch({
                type: "setApiTable",
                apiTable: sourceIds.length
                  ? {
                      sourceIds,
                      merge: apiTable?.merge ?? false,
                      download: apiTable?.download ?? false,
                    }
                  : null,
              });
            }}
          />
          {apiTable && (
            <div className="flex gap-4">
              <Checkbox
                label="Show a download button"
                checked={apiTable.download}
                onChange={download =>
                  dispatch({
                    type: "setApiTable",
                    apiTable: { ...apiTable, download },
                  })
                }
              />
              <Checkbox
                label="Fill the form from selected rows"
                checked={apiTable.merge}
                onChange={merge =>
                  dispatch({
                    type: "setApiTable",
                    apiTable: { ...apiTable, merge },
                  })
                }
              />
            </div>
          )}
          <p className="text-xs text-gray-500">
            Shown on the form once API tables are available.
          </p>
        </div>
      )}
    </div>
  );
};

interface Props {
  canvasId: string;
  /** Null opens a new form. */
  eventId: string | null;
  onClose: () => void;
}

const EventDefinitionEditor = ({ canvasId, eventId, onClose }: Props) => {
  const { setNotification } = useNotifications();
  const optionsQuery = useVmetaEventEditorOptions(canvasId, true);
  const definitionQuery = useVmetaEventDefinition(
    canvasId,
    eventId ?? undefined
  );
  const create = useCreateVmetaEvent();
  const update = useUpdateVmetaEvent();
  const [state, dispatch] = useReducer(
    definitionReducer,
    undefined,
    emptyDefinitionState
  );
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // The event id the form state was last loaded for. The state is reset only when this differs
  // (first load) or on Reload, never by a background refetch, so unsaved edits survive a 409.
  const loadedFor = useRef<string | null>(null);

  const options = optionsQuery.data;
  const definition = definitionQuery.data;
  const key = eventId ?? NEW_FORM;
  const ready = !!options && (eventId === null || !!definition);

  useEffect(() => {
    if (!ready || loadedFor.current === key) return;
    loadedFor.current = key;
    setError(null);
    setStale(null);
    dispatch({
      type: "reset",
      state:
        eventId !== null && definition && options
          ? definitionStateFrom(definition, options)
          : emptyDefinitionState(),
    });
    // Keyed on readiness and the event id only: a refetched definition must not reset the form.
  }, [ready, key]);

  if (optionsQuery.error || definitionQuery.error) {
    return (
      <EditorFrame eventId={eventId} onClose={onClose}>
        <p className="text-sm text-gray-700">
          {eventErrorMessage(
            optionsQuery.error ?? definitionQuery.error,
            "This event form could not be loaded."
          )}
        </p>
      </EditorFrame>
    );
  }
  if (!ready || !options || loadedFor.current !== key) {
    return (
      <EditorFrame eventId={eventId} onClose={onClose}>
        <Spinner loading spinColor="#4f46e5" />
      </EditorFrame>
    );
  }

  const unkept = (definition?.warnings.length ?? 0) > 0 && eventId !== null;
  const collections = new Map(options.collections.map(c => [c.id, c]));
  const collectionOptions = options.collections.map(c => ({
    value: c.id,
    label: c.label || c.name,
  }));
  const presentIds = [...new Set(state.sections.map(s => s.collectionId))];
  const presentOptions = presentIds.map(id => ({
    value: id,
    label: collections.get(id)?.label || collections.get(id)?.name || id,
  }));
  const droppedParts = state.droppedFieldCount + state.droppedSectionCount;
  const seen = new Map<string, number>();
  const ordinals = state.sections.map(s => {
    const n = (seen.get(s.collectionId) ?? 0) + 1;
    seen.set(s.collectionId, n);
    return n;
  });

  const save = async () => {
    setError(null);
    setStale(null);
    setSaving(true);
    const input = inputFromState(state);
    try {
      if (eventId === null) {
        await create.mutateAsync({ canvasId, body: input });
      } else {
        await update.mutateAsync({
          canvasId,
          eventId,
          body: { ...input, version: state.version },
        });
      }
      setNotification({
        id: "event-definition-saved",
        message: `${input.name} saved`,
        variant: NOTIFICATION.SUCCESS,
      });
      onClose();
    } catch (err) {
      if (eventErrorStatus(err) === 409)
        setStale(
          eventErrorMessage(err, "This event form changed since you opened it.")
        );
      else
        setError(eventErrorMessage(err, "The event form could not be saved."));
    } finally {
      setSaving(false);
    }
  };

  const reload = async () => {
    const result = await definitionQuery.refetch();
    if (!result.data) {
      setError("This event form could not be loaded.");
      return;
    }
    setStale(null);
    setError(null);
    dispatch({
      type: "reset",
      state: definitionStateFrom(result.data, options),
    });
  };

  return (
    <EditorFrame eventId={eventId} onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={e => {
          e.preventDefault();
          void save();
        }}>
        {unkept && (
          <MessageBanner
            messages={[EVENT_HAS_UNKEPT_PARTS_MESSAGE]}
            type={BannerType.Error}
          />
        )}
        {stale && (
          <div className="flex items-center justify-between gap-4 rounded-md bg-yellow-50 p-3">
            <p className="text-sm text-yellow-800">{stale}</p>
            <button
              type="button"
              className={buttonClass}
              onClick={() => void reload()}>
              Reload
            </button>
          </div>
        )}
        {error && <MessageBanner messages={[error]} type={BannerType.Error} />}
        {droppedParts > 0 && (
          <p className="text-sm text-gray-700">
            {droppedParts} fields or sections of this form no longer exist and
            will be removed when you save.
          </p>
        )}
        {state.droppedApiSourceCount > 0 && (
          <p className="text-sm text-gray-700">
            {state.droppedApiSourceCount} API tables of this form no longer
            exist and will be removed when you save.
          </p>
        )}
        <div>
          <label
            htmlFor="event-definition-name"
            className="block text-sm font-medium text-gray-900">
            Name
          </label>
          <input
            id="event-definition-name"
            type="text"
            maxLength={200}
            className="mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 sm:text-sm"
            value={state.name}
            onChange={e => dispatch({ type: "setName", name: e.target.value })}
          />
        </div>
        {state.sections.map((section, i) => (
          <SectionCard
            key={i}
            index={i}
            section={section}
            collection={collections.get(section.collectionId)}
            ordinal={ordinals[i]}
            isFirst={i === 0}
            isLast={i === state.sections.length - 1}
            state={state}
            options={options}
            dispatch={dispatch}
          />
        ))}
        <div>
          <p className="text-sm font-medium text-gray-900">Add section</p>
          <SelectComponent
            name="add-section"
            options={collectionOptions}
            value={null}
            placeholder="Choose a collection"
            onChange={selected => {
              const value = (selected as SelectOption | null)?.value;
              if (value !== undefined && value !== null)
                dispatch({ type: "addSection", collectionId: String(value) });
            }}
          />
        </div>
        <div className="space-y-2 rounded-md border border-gray-200 p-4">
          <Checkbox
            label="Choice that shows sections"
            checked={state.dropdown !== null}
            onChange={on =>
              dispatch({
                type: "setDropdown",
                dropdown: on ? { label: "", collectionIds: [] } : null,
              })
            }
          />
          {state.dropdown && (
            <>
              <div>
                <label
                  htmlFor="event-definition-dropdown-label"
                  className="block text-sm font-medium text-gray-900">
                  Label
                </label>
                <input
                  id="event-definition-dropdown-label"
                  type="text"
                  className="mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 sm:text-sm"
                  value={state.dropdown.label}
                  onChange={e =>
                    state.dropdown &&
                    dispatch({
                      type: "setDropdown",
                      dropdown: { ...state.dropdown, label: e.target.value },
                    })
                  }
                />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Collections it shows
                </p>
                <SelectComponent
                  name="dropdown-collections"
                  isMulti
                  options={presentOptions}
                  value={optionsOf(
                    state.dropdown.collectionIds,
                    presentOptions
                  )}
                  onChange={selected =>
                    state.dropdown &&
                    dispatch({
                      type: "setDropdown",
                      dropdown: {
                        ...state.dropdown,
                        collectionIds: multiValues(selected),
                      },
                    })
                  }
                />
              </div>
            </>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" className={buttonClass} onClick={onClose}>
            Cancel
          </button>
          {!unkept && (
            <button type="submit" className={primaryClass} disabled={saving}>
              Save
            </button>
          )}
        </div>
      </form>
    </EditorFrame>
  );
};

const EditorFrame = ({
  eventId,
  onClose,
  children,
}: {
  eventId: string | null;
  onClose: () => void;
  children: ReactNode;
}) => (
  <div className="flex h-full flex-col">
    <div className="flex items-center justify-between border-b border-gray-200 p-4">
      <h3 className="text-lg font-semibold leading-6 text-gray-900">
        {eventId === null ? "New event form" : "Edit event form"}
      </h3>
      <button
        type="button"
        className="rounded-md text-gray-900 hover:text-gray-600"
        onClick={onClose}
        title="Close drawer">
        <span className="sr-only">Close</span>
        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
      </button>
    </div>
    <div className="flex-1 overflow-y-auto p-4">{children}</div>
  </div>
);

export default EventDefinitionEditor;
